import { type CartState, initialCartState } from "../types";
import { type CartEntry, isPlainLineItem, isSubLineItem, type PlainLineItem } from "../../models/CartData";
import { CHECKOUT_LOAD_DATA } from "../actions/checkoutPageActions";
import { type CartActions } from "../actions/cartQuoteCommonActions";
import { INVALID_ENTRY_ID } from "~/features/quickEntry/constants/constants";
import { getNextUID } from "~/common/utils/uid";
import { userIsSalesman } from "~/domains/user/utils/roles";
import { produce } from "immer";
import { CART_LOAD_SUCCESS } from "~/features/cart/cart/redux/actions";
import { UPDATE_CART_COMMENT } from "~/features/cart/cart/redux/actions/cartPageActions";
import { isCartPackEntryValid, isQuotePackEntryValid } from "~/features/cart/cart/redux/reducers/lineItemValidations";
import { CLEAR_CART, DELETE_PACKAGE_ENTRY, SET_PACKAGE_ENTRIES } from "~/features/cart/cart/redux/actions/actionTypes";

const isPackEntryValid = userIsSalesman() ? isQuotePackEntryValid : isCartPackEntryValid;

export const cartReducer = (state = initialCartState, action: CartActions): CartState => {
  switch (action.type) {
    case CART_LOAD_SUCCESS: {
      const data = action.payload;

      return {
        ...state,
        packEntriesById: data.packEntriesById,
        cartEntriesById: data.cartEntriesById,
        cartEntries: data.cartEntries,
        comment: data.comment,
      };
    }

    case CHECKOUT_LOAD_DATA: {
      const { data } = action.payload;

      return {
        ...state,
        packEntriesById: data.packEntriesById,
        cartEntriesById: data.cartEntriesById,
        cartEntries: data.cartEntries,
      };
    }

    case SET_PACKAGE_ENTRIES: {
      const data = action.payload;

      let cartEntriesById = state.cartEntriesById;
      const packEntriesById = { ...state.packEntriesById };

      const newCartEntriesByCode: Dict<CartEntry> = {};
      data.forEach((item) => {
        const packageEntry = item.packageEntry;

        if (packageEntry.cartEntryId === INVALID_ENTRY_ID && packageEntry.cartEntryNumber === INVALID_ENTRY_ID) {
          const productCode = item.productCode;

          let cartEntry = newCartEntriesByCode[productCode];
          if (!cartEntry) {
            cartEntry = newCartEntriesByCode[productCode] = {
              pk: -1,
              id: getNextUID(),
              productCode,
              packEntryIds: [],
              entryNumber: Object.keys(cartEntriesById).length,
            };
            cartEntriesById = { ...cartEntriesById, [cartEntry.id]: cartEntry };
          }
          packageEntry.cartEntryId = cartEntry.id;
          packageEntry.cartEntryNumber = cartEntry.entryNumber;
        }

        const packageEntryId = packageEntry.id;
        const cartEntry = cartEntriesById[packageEntry.cartEntryId];

        if (cartEntry.packEntryIds.indexOf(packageEntryId) === -1) {
          if (cartEntriesById === state.cartEntriesById) {
            cartEntriesById = { ...state.cartEntriesById };
          }
          cartEntriesById[cartEntry.id] = {
            ...cartEntry,
            packEntryIds: cartEntry.packEntryIds.concat(packageEntryId),
          };
        }

        const packEntry = { ...item.packageEntry };
        packEntry.valid = isPackEntryValid(packEntry);
        packEntry.disableEdit =
          (packEntry.contractErrorMessage && packEntry.contractErrorMessage.type === "fatal") ||
          packEntry.messages.some((msg) => msg.type === "fatal");
        packEntriesById[packageEntryId] = packEntry;
      });

      let cartEntries = state.cartEntries;
      if (Object.values(newCartEntriesByCode).length > 0) {
        cartEntries = [...cartEntries, ...Object.values(newCartEntriesByCode).map((item) => item.id)];
      }

      return {
        ...state,
        cartEntriesById,
        packEntriesById,
        cartEntries,
      };
    }

    case DELETE_PACKAGE_ENTRY: {
      const { packageEntryId, isKIT } = action.payload;

      return produce(state, (draft: typeof state) => {
        const getPackage = (id: number) => draft.packEntriesById[id];
        const getCartEntry = (id: number) => draft.cartEntriesById[id];

        const packageEntry = getPackage(packageEntryId);
        if (!packageEntry) {
          console.warn(`Tried to delete package with id ${packageEntryId} but it's already deleted!`);
          return;
        }

        const packagesForDeletion = [packageEntry];

        if (isPlainLineItem(packageEntry) && packageEntry.optionalPartIds.length) {
          // Delete all pack entries that have this one as parent
          packagesForDeletion.push(...packageEntry.optionalPartIds.map(getPackage));
        } else if (isSubLineItem(packageEntry)) {
          // Delete itself from the parent list
          const parentEntry = getPackage(packageEntry.parentLineItemId) as PlainLineItem;
          parentEntry.optionalPartIds = parentEntry.optionalPartIds.filter((id) => id !== packageEntry.id);

          if (isKIT && !parentEntry.optionalPartIds.length) {
            // This is a KIT product, if it doesn't have any optional parts, it needs to be deleted
            packagesForDeletion.push(parentEntry);
          }
        }

        packagesForDeletion.forEach((packageEntry) => {
          const cartEntry = getCartEntry(packageEntry.cartEntryId);
          cartEntry.packEntryIds = cartEntry.packEntryIds.filter((id) => id !== packageEntry.id);

          if (!cartEntry.packEntryIds.length) {
            delete draft.cartEntriesById[cartEntry.id];
            draft.cartEntries = draft.cartEntries.filter((id) => id !== cartEntry.id);
          } else {
            // Update pack entries' entry numbers so they reflect the values on BE after deleting an entry
            cartEntry.packEntryIds
              .map(getPackage)
              .filter((entry) => entry.entryNumber !== null) // Skip sub line items
              .forEach((entry, index) => (entry.entryNumber = index));
          }
          delete draft.packEntriesById[packageEntry.id];
        });

        // Update product entries' entry numbers so they reflect the values on BE after deleting a product
        draft.cartEntries
          .map((id) => draft.cartEntriesById[id])
          .filter((entry) => entry.entryNumber !== INVALID_ENTRY_ID) // Skip fake cart entries
          .forEach((entry, index) => {
            entry.entryNumber = index;
            entry.packEntryIds.forEach((id) => {
              const packEntry = draft.packEntriesById[id];
              packEntry.cartEntryNumber = entry.entryNumber;
            });
          });
      });
    }

    case CLEAR_CART: {
      return {
        ...state,
        cartEntries: [],
        cartEntriesById: {},
        packEntriesById: {},
      };
    }

    case UPDATE_CART_COMMENT: {
      const comment = action.payload;
      return {
        ...state,
        comment,
      };
    }

    default:
      return state;
  }
};
